// render-my-wishlist.ts — the "catalog-my-wishlist" renderer: the viewer's saved source wishlist.
//
// The endpoint ships pure source-card data; this renderer owns the compact card layout, source media,
// product-type badges, and the two actions: move to cart (normal POST/reload) and remove (AJAX).

import type { CatalogListConfig, CatalogMyWishlistResponse, MyWishlistCard } from './types';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';
import { buildEmptyState, emptyStateFromAttributes } from '../shared/empty-state';
import { buildSourceMediaNode, resolveSourceMedia } from '../source-media/core';

const SOURCE_MEDIA_STYLE = 'width: 100%; height: 100%; object-fit: cover; object-position: top; vertical-align: top;';

function icon(id: string, w = 20, h = 20, title: string | boolean = false): string {
    return typeof iconMap === 'function' ? iconMap(id, w, h, title) : '';
}

function syncWishlistDropdownBadge(count: number): void {
    document.querySelectorAll('a.dropdown-item[href$="my_wishlist.php"]').forEach((link) => {
        let badge = link.querySelector('.badge');
        if (count > 0) {
            if (!badge) {
                badge = document.createElement('span');
                badge.className = 'badge bg-secondary rounded-pill';
                link.appendChild(document.createTextNode(' '));
                link.appendChild(badge);
            }
            badge.textContent = String(count);
        } else if (badge) {
            const prev = badge.previousSibling;
            if (prev && prev.nodeType === 3 && /^\s$/.test(String(prev.nodeValue || ''))) {
                prev.remove();
            }
            badge.remove();
        }
    });
}

function postMoveToCart(sourceId: string): void {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = resolveUrl('my_wishlist.php');
    form.className = 'd-none';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'move_to_cart_source_id';
    input.value = sourceId;
    form.appendChild(input);

    document.body.appendChild(form);
    form.submit();
}

function typeIcon(typeLabel: string): string {
    return typeLabel.toLowerCase().includes('adventure') ? 'adventure' : 'source';
}

function renderWishlistCard(card: MyWishlistCard, defaultCoverBase: string, onRemove: (card: MyWishlistCard, el: HTMLElement) => Promise<void>): HTMLElement {
    const media = resolveSourceMedia(card.pictures, card.videos, defaultCoverBase);
    const col = document.createElement('div');
    col.className = 'col-12 col-md-6 col-lg-3 d-flex align-items-start px-2 py-2 mb-2 js-my-wishlist-card';
    col.dataset.sourceName = card.name || '';

    const shell = document.createElement('div');
    shell.className = 'card text-left w-100 h-100 shadow-sm d-flex flex-column ft-source-catalog-card border border-secondary border-opacity-25'
        + (media.kind === 'video' ? ' video-card-hover' : '');
    shell.setAttribute('data-bs-theme', 'dark');
    col.appendChild(shell);

    const mediaLink = document.createElement('a');
    mediaLink.href = card.url;
    mediaLink.className = 'ft-source-catalog-card__media d-flex align-items-center justify-content-center bg-dark flex-shrink-0 w-100 position-relative text-decoration-none';
    mediaLink.setAttribute('style', 'overflow: hidden; aspect-ratio: 10 / 13;');
    mediaLink.setAttribute('aria-label', `View ${card.name || 'source'}`);
    mediaLink.appendChild(buildSourceMediaNode({
        pictures: card.pictures,
        videos: card.videos,
        defaultCoverBase,
        mediaClass: 'w-100 d-block',
        mediaStyle: SOURCE_MEDIA_STYLE,
        alt: card.name || '',
    }));
    shell.appendChild(mediaLink);

    const body = document.createElement('div');
    body.className = 'card-body py-2 px-3 d-flex flex-column gap-2';
    shell.appendChild(body);

    const title = document.createElement('a');
    title.href = card.url;
    title.className = 'card-title fs-6 lh-sm mb-0 fw-semibold text-white text-decoration-none';
    title.textContent = card.name || 'Untitled';
    body.appendChild(title);

    if (Array.isArray(card.product_type_badges) && card.product_type_badges.length) {
        const badges = document.createElement('div');
        badges.className = 'd-flex flex-wrap gap-1';
        badges.setAttribute('aria-label', 'Product types');
        for (const pt of card.product_type_badges) {
            if (!pt.name) continue;
            const badge = document.createElement('span');
            badge.className = 'badge bg-secondary bg-opacity-75 fw-normal text-wrap';
            badge.textContent = pt.name;
            badges.appendChild(badge);
        }
        body.appendChild(badges);
    }

    const footer = document.createElement('div');
    footer.className = 'card-footer ft-source-catalog-card__footer d-flex align-items-center justify-content-between gap-2 px-3 py-2 border-top border-secondary border-opacity-25 bg-transparent mt-auto';
    shell.appendChild(footer);

    const price = document.createElement('span');
    price.className = 'ft-source-catalog-card__price fw-semibold lh-sm';
    const priceInner = document.createElement('span');
    priceInner.className = card.price_class || 'text-success';
    priceInner.textContent = card.price_label || 'Free';
    price.appendChild(priceInner);
    footer.appendChild(price);

    const typeLabel = (card.type_label || 'Source').trim() || 'Source';
    const type = document.createElement('span');
    type.className = 'badge bg-info-subtle text-info-emphasis border border-info border-opacity-25 fw-normal d-inline-flex align-items-center gap-1';
    type.innerHTML = icon(typeIcon(typeLabel), 16, 16, false);
    type.appendChild(document.createTextNode(typeLabel));
    footer.appendChild(type);

    const actions = document.createElement('div');
    actions.className = 'border-top border-secondary border-opacity-25 px-3 py-2 d-flex flex-wrap align-items-center justify-content-between gap-2';
    shell.appendChild(actions);

    const move = document.createElement('button');
    move.type = 'button';
    move.className = 'btn btn-sm btn-warning text-dark d-inline-flex align-items-center gap-1';
    move.innerHTML = icon('add_to_cart', 20, 20, false) + '<span>Move to cart</span>';
    move.addEventListener('click', () => postMoveToCart(card.item_id));
    actions.appendChild(move);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'btn btn-sm btn-danger d-inline-flex align-items-center justify-content-center px-2';
    remove.setAttribute('aria-label', 'Remove from wishlist');
    remove.innerHTML = icon('delete', 22, 22, 'Remove');
    remove.addEventListener('click', () => { void onRemove(card, col); });
    actions.appendChild(remove);

    actions.querySelectorAll<HTMLElement>('.btn').forEach((btn) => {
        btn.style.minHeight = '2rem';
    });

    return col;
}

interface BuiltCard { el: HTMLElement; text: string; card: MyWishlistCard; }

export function mountCatalogMyWishlist(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) {
        console.error('[catalog-my-wishlist] mount missing [data-ft-catalog-results]', root);
        return;
    }

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;
    const emptyText = root.getAttribute('data-ft-empty-text') || 'Your wishlist is empty.';
    const emptySearchText = root.getAttribute('data-ft-empty-search-text') || 'No wishlist items match your search.';

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            const raw = await res.text();
            let data: CatalogMyWishlistResponse;
            try {
                data = JSON.parse(raw) as CatalogMyWishlistResponse;
            } catch {
                throw new Error(`Non-JSON response (HTTP ${res.status}): ${raw.slice(0, 300)}`);
            }
            const errData = data as unknown as { error?: string; detail?: string };
            if (!res.ok || errData.error) {
                throw new Error(errData.detail || errData.error || `HTTP ${res.status}`);
            }
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            build(root, container, data, searchInput, emptyText, emptySearchText);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-my-wishlist] fetch/render failed', e);
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'col-12 text-center text-muted py-4';
            msg.textContent = 'Could not load wishlist. Please try again.';
            container.appendChild(msg);
        }
    })();
}

function build(
    root: HTMLElement,
    container: HTMLElement,
    data: CatalogMyWishlistResponse,
    searchInput: HTMLInputElement | null,
    emptyText: string,
    emptySearchText: string,
): void {
    container.innerHTML = '';

    const built: BuiltCard[] = [];
    const removeUrl = resolveUrl('options/tools/sources/wishlist/remove_from_wishlist_inc.php');

    const applyFilter = (): void => {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        let visible = 0;
        for (const c of built) {
            const show = !keyword || c.text.includes(keyword);
            c.el.style.display = show ? '' : 'none';
            if (show) visible++;
        }
        if (!emptySpec) emptyEl.textContent = keyword ? emptySearchText : emptyText;
        emptyEl.style.display = visible === 0 ? '' : 'none';
    };

    const onRemove = async (_card: MyWishlistCard, el: HTMLElement): Promise<void> => {
        const button = el.querySelector('button[aria-label="Remove from wishlist"]') as HTMLButtonElement | null;
        if (button?.disabled) return;
        if (button) button.disabled = true;
        try {
            const res = await fetch(removeUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ source_id: _card.item_id }),
            });
            const json = await res.json().catch(() => null) as { ok?: boolean; wishlist_count?: number } | null;
            if (!json || json.ok !== true) {
                if (button) button.disabled = false;
                return;
            }
            const nextCount = Number.isFinite(Number(json.wishlist_count)) ? Number(json.wishlist_count) : 0;
            syncWishlistDropdownBadge(nextCount);
            const idx = built.findIndex((c) => c.el === el);
            if (idx >= 0) built.splice(idx, 1);
            el.remove();
            applyFilter();
        } catch {
            if (button) button.disabled = false;
        }
    };

    for (const card of data.cards || []) {
        const el = renderWishlistCard(card, data.default_cover_base || '', onRemove);
        container.appendChild(el);
        built.push({ el, card, text: card.text || (card.name || '').toLowerCase() });
    }

    initTooltips(container);
    const initVideos = (window as unknown as { initializeVideoCards?: () => void }).initializeVideoCards;
    if (typeof initVideos === 'function') initVideos();

    const emptyEl = document.createElement('div');
    emptyEl.className = 'col-12 text-center text-muted py-5';
    emptyEl.textContent = emptyText;
    emptyEl.style.display = built.length ? 'none' : '';
    container.appendChild(emptyEl);
    const emptySpec = built.length === 0 ? emptyStateFromAttributes(root, emptyText) : null;
    if (emptySpec) {
        emptyEl.className = 'col-12';
        emptyEl.replaceChildren(buildEmptyState(emptySpec));
    }

    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = (): void => {
            const has = (searchInput.value || '').length > 0;
            if (glyph instanceof HTMLElement) glyph.style.display = has ? 'none' : '';
            if (clear instanceof HTMLElement) clear.style.display = has ? '' : 'none';
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
        clear?.addEventListener('click', () => {
            searchInput.value = '';
            searchInput.focus();
            syncTrailing();
            applyFilter();
        });
        syncTrailing();
    }
    applyFilter();
}
